/**
 * Match Handlers Utility
 *
 * Centralized handlers for game/match-related operations including:
 * - Building teams data for live games
 * - Opening/closing live game tabs
 * - Completing games and updating tournament state
 */

import { tournamentAPI, tournamentScheduleAPI } from '../services/tournamentApi';
import { GAME_STATUS, CHECKIN_STATUS } from '../constants';
import { apiCall } from '../../services/apiUtil';

/**
 * MatchHandlers class - Encapsulates game/match operation logic
 */
export class MatchHandlers {
  constructor(config = {}) {
    this.user = config.user;
  }

  /**
   * Build teams data from a game for the scoring interface
   * Fetches full team/player details for both participants
   */
  async buildTeamsDataFromGame(game) {
    const teamsData = {
      members: [],
      initialTeams: []
    };

    try {
      // Fetch participant A data (team or user)
      if (game.participant_a_type === 'team' && game.participant_a_id) {
        try {
          const teamA = await apiCall(`/teams/${game.participant_a_id}`);
          const teamAPlayers = (teamA.members || []).map(m => ({
            id: m.user.id,
            name: `${m.user.firstName} ${m.user.lastName}`.trim(),
            firstName: m.user.firstName,
            lastName: m.user.lastName,
            profileImage: m.user.profileImage
          }));
          teamsData.members.push(...teamAPlayers);
          teamsData.initialTeams.push({
            id: game.participant_a_id,
            name: game.participant_a_name || teamA.team_name || 'Team A',
            players: teamAPlayers
          });
        } catch (e) {
          console.error('[MatchHandlers] Error fetching team A:', e);
        }
      } else if (game.participant_a_type === 'user' && game.participant_a_id) {
        // Individual user - create a team with one player
        teamsData.initialTeams.push({
          id: `user-a-${game.participant_a_id}`, // TODO: Should use individual participant structure
          name: game.participant_a_name || 'Player A',
          players: [{ id: game.participant_a_id, name: game.participant_a_name }]
        });
        teamsData.members.push({ id: game.participant_a_id, name: game.participant_a_name });
      }

      // Fetch participant B data (team or user)
      if (game.participant_b_type === 'team' && game.participant_b_id) {
        try {
          const teamB = await apiCall(`/teams/${game.participant_b_id}`);
          const teamBPlayers = (teamB.members || []).map(m => ({
            id: m.user.id,
            name: `${m.user.firstName} ${m.user.lastName}`.trim(),
            firstName: m.user.firstName,
            lastName: m.user.lastName,
            profileImage: m.user.profileImage
          }));
          teamsData.members.push(...teamBPlayers);
          teamsData.initialTeams.push({
            id: game.participant_b_id,
            name: game.participant_b_name || teamB.team_name || 'Team B',
            players: teamBPlayers
          });
        } catch (e) {
          console.error('[MatchHandlers] Error fetching team B:', e);
        }
      } else if (game.participant_b_type === 'user' && game.participant_b_id) {
        // Individual user - create a team with one player
        teamsData.initialTeams.push({
          id: `user-b-${game.participant_b_id}`, // TODO: Should use individual participant structure
          name: game.participant_b_name || 'Player B',
          players: [{ id: game.participant_b_id, name: game.participant_b_name }]
        });
        teamsData.members.push({ id: game.participant_b_id, name: game.participant_b_name });
      }

      // Validate that we have teams/participants
      if (teamsData.initialTeams.length === 0) {
        throw new Error('Teams or participants not setup properly');
      }

      return teamsData;
    } catch (error) {
      console.error('[MatchHandlers] Error building teams data:', error);
      throw new Error('Failed to build teams data: ' + error.message);
    }
  }

  /**
   * Complete a game and record the result
   * Updates game status, records result, and triggers tournament refresh
   */
  async completeGame(gameId, result, tournament) {

    try {
      // Record the game result - this will:
      // 1. Update tournament game status to completed
      // 2. Update event status to completed
      // 3. Update standings
      // 4. Advance winner in bracket if applicable
      if (result && result.winnerId && result.winnerType) {
        await tournamentScheduleAPI.recordResult(gameId, {
          winnerId: result.winnerId,
          winnerType: result.winnerType,
          scoreSummary: result.scoreSummary || `${result.playerAScore}-${result.playerBScore}`
        });
      } else {
        // Fallback: just update status if no result data
        await tournamentScheduleAPI.updateGame(gameId, {
          status: GAME_STATUS.COMPLETED
        });
      }

      // Return updated tournament data
      const updatedTournament = await tournamentAPI.getById(tournament.id);
      return updatedTournament;
    } catch (error) {
      console.error('[MatchHandlers] Failed to complete game:', error);
      throw error;
    }
  }
}

export default MatchHandlers;
